import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { authService } from '@/services/auth-service';
import { useAuthStore } from '@/stores/auth-store';
import BannerDisplay from '@/components/BannerDisplay';
import OfficialSourcesNotice from '@/components/OfficialSourcesNotice';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import { 
  Package,
  ListChecks,
  Calendar,
  Users,
  ArrowRight,
  Smartphone,
  Monitor,
  X,
} from 'lucide-react';

export default function HomePage() {
  const { t } = useTranslation();
  const { isAuthenticated, _hasHydrated } = useAuthStore();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showOnboarding) {
      document.documentElement.classList.add('modal-open');
    } else {
      document.documentElement.classList.remove('modal-open');
    }
    return () => { document.documentElement.classList.remove('modal-open'); };
  }, [showOnboarding]);

  const startLogin = async () => {
    setIsLoggingIn(true);
    try {
      const { authorizationUrl } = await authService.getOAuthUrl('microsoft', { prompt: 'login' });
      window.location.href = authorizationUrl;
    } catch (err) {
      console.error('Failed to initiate Entra B2C login:', err);
      alert('Kunde inte starta inloggning. Kontrollera din internetanslutning och försök igen.');
      setIsLoggingIn(false);
    }
  };

  // Sätt sidtitel för publik startsida
  useEffect(() => {
    document.title = 'Beredskapshubben - Sveriges beredskapsapp';
  }, []);

  // Vänta på att auth-state har laddats från localStorage
  if (!_hasHydrated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status">
            <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Laddar...</span>
          </div>
        </div>
      </div>
    );
  }

  // Omdirigera till dashboard om redan inloggad
  if (isAuthenticated) {
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      {/* Onboarding-modal */}
      {showOnboarding && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
          onClick={() => setShowOnboarding(false)}
        >
          <div
            className="relative w-full max-w-md rounded-2xl border border-border bg-background p-8 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowOnboarding(false)}
              className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Stäng"
            >
              <X className="h-5 w-5" />
            </button>

            <h2 className="text-2xl font-bold text-foreground mb-8">Enkelt att komma igång</h2>

            <ol className="space-y-6">
              {[
                'Logga in eller skapa ett nytt konto.',
                'Vi skickar en engångskod till din e-postadress.',
                'Ange koden för att komma vidare.',
              ].map((text, i) => (
                <li key={i} className="flex items-center gap-4">
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white font-bold text-lg"
                    style={{ backgroundColor: '#7a8a78' }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-foreground text-base">{text}</span>
                </li>
              ))}
            </ol>

            <button
              onClick={startLogin}
              disabled={isLoggingIn}
              className="mt-10 w-full inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 text-lg font-medium text-white transition-all btn-retro hover:brightness-110 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#67724e' }}
            >
              {isLoggingIn ? 'Ansluter…' : 'Logga in / Skapa konto'}
            </button>

            <p className="mt-3 text-center text-xs text-muted-foreground/80">
              Inloggningssidan levereras i samarbete med Microsoft
            </p>
          </div>
        </div>
      )}

      {/* Prevent scroll when modal is open - handled via useEffect */}

      <section className="pt-6 pb-12 md:pt-8 md:pb-20 relative overflow-hidden">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover opacity-60"
          style={{ backgroundImage: 'url(/background.png)', backgroundPosition: 'center 80%' }}
        />
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-neon-900/10 to-transparent pointer-events-none" />
        <div className="container mx-auto px-4 text-center relative z-10">
          
          {/* Bannermeddelanden - högst upp i hero-sektionen */}
          <div className="mb-6">
            <div className="w-full max-w-2xl md:max-w-4xl mx-auto">
              <BannerDisplay location="login" compact />
            </div>
          </div>

          <div className="inline-block bg-black/70 backdrop-blur-sm rounded-lg px-6 py-8 md:px-10 md:py-10">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl neon-glow">
              {t('home.hero.title')}
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base md:text-lg text-muted-foreground">
                {(() => {
                  const sub = t('home.hero.subtitle');
                  const target = 'Beredskapshubben';
                  const idx = sub.indexOf(target);
                  if (idx === -1) return sub;
                  return (
                    <>
                      {sub.substring(0, idx)}
                      <span style={{ color: '#d17f0a' }}>{target}</span>
                      {sub.substring(idx + target.length)}
                    </>
                  );
                })()}
            </p>
            <button
              onClick={() => setShowOnboarding(true)}
              className="mt-8 inline-flex items-center gap-2 rounded-md px-6 py-3 text-lg font-medium text-white transition-all btn-retro hover:brightness-110 hover:scale-105"
              style={{ backgroundColor: '#67724e' }}
            >
              {t('home.hero.cta')}
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-border bg-muted/30 py-6">
        <div className="container mx-auto px-4">
          <h2 className="text-center text-2xl md:text-3xl font-bold">
            {t('home.features.title')}
          </h2>
          <div className="mt-6 md:mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <FeatureCard
              icon={Package}
              title={t('home.features.checklists.title')}
              description={t('home.features.checklists.description')}
              href="https://blogg.beredskapshubben.se/post/forrad-guide"
            />
            <FeatureCard
              icon={ListChecks}
              title={t('home.features.knowledge.title')}
              description={t('home.features.knowledge.description')}
              href="https://blogg.beredskapshubben.se/post/checklista-guide"
            />
            <FeatureCard
              icon={Calendar}
              title={t('home.features.vma.title')}
              description={t('home.features.vma.description')}
              href="https://blogg.beredskapshubben.se/post/arshjul-guide"
            />
            <FeatureCard
              icon={Users}
              title={t('home.features.family.title')}
              description={t('home.features.family.description')}
              href="https://blogg.beredskapshubben.se/post/familjelage-guide"
            />
          </div>
        </div>
      </section>

      {/* Platform Support */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Tillgänglig på alla dina enheter
            </h2>
            <p className="text-muted-foreground mb-8">
              Använd Beredskapshubben på din iPhone, Android-telefon eller i webbläsaren på din dator.
            </p>
            <div className="flex flex-wrap justify-center gap-8 md:gap-12">
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
                  <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                </div>
                <span className="font-medium">iOS</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
                  <Smartphone className="w-8 h-8 text-primary" />
                </div>
                <span className="font-medium">Android</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
                  <Monitor className="w-8 h-8 text-primary" />
                </div>
                <span className="font-medium">Webb</span>
              </div>
            </div>

            <OfficialSourcesNotice
              className="mt-10 text-left"
              title="Officiella källor och viktig information"
              description="Beredskapshubben är ett privat stödverktyg för hemberedskap. För aktuella råd, instruktioner och krisinformation ska du alltid kontrollera officiella svenska källor."
            />
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

interface FeatureCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  href: string;
}

function FeatureCard({ icon: Icon, title, description, href }: FeatureCardProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="block rounded-lg bg-card border border-border p-6 box-glow-subtle hover:border-primary/50 transition-all duration-300"
    >
      <div className="mb-3 flex items-center gap-3">
        <div className="inline-flex rounded-lg bg-primary/20 p-2">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <h3 className="font-semibold">{title}</h3>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </a>
  );
}
